import { type AnnouncementBannerProps } from '@molecules/Alerts/AnnouncementBanner'

import type { ComponentDoc, PropDocs } from '../types'

const props: PropDocs<AnnouncementBannerProps> = {
  spriteUrl: {
    type: 'string',
    description: 'Only for the legacy island. The ERB layout passes the fingerprinted sprite path so the icons load in production.',
  },
  announcement: {
    type: '{ id: string; href: string } | null',
    default: 'CURRENT_ANNOUNCEMENT',
    description: 'The announcement to show, or null for none. Do not pass it; the layouts use the default. Tests pass one in.',
  },
}

const doc: ComponentDoc<AnnouncementBannerProps> = {
  slug: 'announcement-banner',
  name: 'AnnouncementBanner',
  tier: 'molecule',
  description:
    'A one-line notice fixed across the top of every signed-in page, with a link to read more. The sidebar and page sit below it. Closing it hides it in that browser. Both layouts always mount it; it renders nothing while CURRENT_ANNOUNCEMENT in the component is null.',
  importStatement: "import { AnnouncementBanner } from '@molecules/Alerts/AnnouncementBanner'",
  sourcePath: 'app/frontend/components/molecules/Alerts/AnnouncementBanner.tsx',
  props,
  examples: [
    {
      title: 'Current announcement',
      note: 'No announcement is set right now, so nothing shows. When one is, it is the strip at the top of every signed-in page, this one included. If you closed it, clear site data for this site to bring it back.',
      render: () => null,
      code: '<AnnouncementBanner />',
    },
  ],
  usage: {
    do: [
      'To announce something, set CURRENT_ANNOUNCEMENT to { id, href } and put its body and read_more in config/locales/v2/views/components/announcement_banner.en.yml. A new id shows the banner again to everyone who closed the last one.',
      'Keep the wording to one sentence and put the detail behind the link.',
      'To take it down, set CURRENT_ANNOUNCEMENT back to null and delete body and read_more. Leave the component mounted.',
    ],
    dont: [
      'Render it on a page. Both layouts already do.',
      'Use it for errors or for anything the user must act on. That is an Alert in the page.',
    ],
  },
  a11y: [
    'The link opens in a new tab.',
    'The close button is labelled Close and works from the keyboard.',
  ],
}

export default doc
